import { useState, useRef, useEffect } from 'react';
import { SIDEBAR_MODE } from '../../../constants/types';

/**
 * Shared form state management for event and milestone forms.
 * Handles common fields: title, description, projectId.
 * 
 * @param {Object} options
 * @param {string} options.mode - 'create' or 'edit'
 * @param {Object} options.data - Form data for edit mode
 * @param {string} options.selectedProjectId - Pre-selected project for new items
 * @param {Array} options.projects - Available projects
 * @param {Function} options.onChange - Callback for live preview updates
 * @returns {Object} Form state and handlers
 */
function useFormState({ mode, data, selectedProjectId, projects, onChange }) {
  // Calculate default project ID - use selectedProjectId for new items
  const getDefaultProjectId = () => selectedProjectId || projects.find(p => p.visible)?.id || '';
  
  // Initialize form values
  const initialTitle = mode === SIDEBAR_MODE.EDIT && data ? (data.title || '') : '';
  const initialDescription = mode === SIDEBAR_MODE.EDIT && data ? (data.description || '') : '';
  const initialProjectId = mode === SIDEBAR_MODE.EDIT && data ? (data.projectId || '') : getDefaultProjectId();

  const [title, setTitle] = useState(initialTitle);
  const [description, setDescription] = useState(initialDescription);
  const [projectId, setProjectId] = useState(initialProjectId);
  
  const inputRef = useRef(null);

  // Focus input on mount
  useEffect(() => {
    const timer = setTimeout(() => {
      inputRef.current?.focus();
      if (mode === SIDEBAR_MODE.EDIT) {
        inputRef.current?.select();
      }
    }, 100);
    return () => clearTimeout(timer);
  }, [mode]);

  // Notify parent of changes for live preview (only in edit mode)
  const notifyChange = (updates = {}) => {
    if (onChange && mode === SIDEBAR_MODE.EDIT) {
      onChange(updates);
    }
  };

  const handleTitleChange = (newTitle) => {
    setTitle(newTitle);
    notifyChange({ title: newTitle });
  };

  const handleDescriptionChange = (newDescription) => {
    setDescription(newDescription);
    notifyChange({ description: newDescription });
  };

  const handleProjectChange = (newProjectId) => {
    setProjectId(newProjectId);
    notifyChange({ projectId: newProjectId });
  };

  return {
    title,
    description,
    projectId,
    handleTitleChange,
    handleDescriptionChange,
    handleProjectChange,
    notifyChange,
    inputRef
  };
}

export default useFormState;
